import React, { useEffect, useState } from 'react';
import { Calendar as CalendarIcon, User, Mail, Phone, Clock, Trash2, Plus, X, Calendar } from 'lucide-react';
import { onboardingCallsService, OnboardingCall, ConsolidatedScheduleBlock } from '../../services/onboardingCallsService';
import Button from '../../components/ui/Button';

const PartnerOnboardingCalls: React.FC = () => {
  const [scheduledCalls, setScheduledCalls] = useState<OnboardingCall[]>([]);
  const [scheduleBlocks, setScheduleBlocks] = useState<ConsolidatedScheduleBlock[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [showBlockForm, setShowBlockForm] = useState(false);
  const [selectedHours, setSelectedHours] = useState<number[]>([]);
  const [blockFormError, setBlockFormError] = useState<string>('');
  const [newBlock, setNewBlock] = useState({
    date: '',
    reason: ''
  });

  // Add this function to handle toast notifications
  const showToast = (message: string, type: 'error' | 'success') => {
    alert(message);
  };

  useEffect(() => {
    fetchScheduledCalls();
    fetchScheduleBlocks();
  }, []);

  const fetchScheduledCalls = async () => {
    try {
      setLoading(true);
      const scheduled = await onboardingCallsService.getScheduledCalls();
      setScheduledCalls(scheduled);
    } catch (error: any) {
      console.error('Error fetching scheduled calls:', error);
      showToast(error.message || 'Failed to fetch scheduled calls', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchScheduleBlocks = async () => {
    try {
      // Use consolidated blocks which automatically filters and combines manual blocks
      const consolidatedBlocks = await onboardingCallsService.getConsolidatedManualBlocks();
      setScheduleBlocks(consolidatedBlocks);
    } catch (error: any) {
      console.error('Error fetching schedule blocks:', error);
      showToast(error.message || 'Failed to fetch schedule blocks', 'error');
    }
  };

  const handleDeleteCall = async (callId: string) => {
    if (!window.confirm('Are you sure you want to delete this call?')) {
      return;
    }

    try {
      await onboardingCallsService.deleteCalendarBooking(callId);
      await fetchScheduledCalls();
      alert('Call deleted successfully');
    } catch (error) {
      console.error('Error deleting call:', error);
      alert('Failed to delete call. Please try again.');
    }
  };

  const checkOverlapWithBookings = async (date: string, hours: number[]): Promise<{ hasOverlap: boolean; overlappingSlots: string[] }> => {
    try {
      // Get all schedule blocks including auto-generated ones
      const allBlocks = await onboardingCallsService.getScheduleBlocks(date, date);
      
      const overlappingSlots: string[] = [];
      
      for (const hour of hours) {
        const startTime = `${hour.toString().padStart(2, '0')}:00`;
        const endTime = `${(hour + 1).toString().padStart(2, '0')}:00`;
        
        // Check if this hour overlaps with any existing block
        const hasOverlap = allBlocks.some(block => {
          if (block.blocked_date !== date) return false;
          
          const blockStart = parseInt(block.start_time.split(':')[0]);
          const blockEnd = parseInt(block.end_time.split(':')[0]);
          
          // Check if the hour falls within an existing block
          return hour >= blockStart && hour < blockEnd;
        });
        
        if (hasOverlap) {
          overlappingSlots.push(`${startTime}-${endTime}`);
        }
      }
      
      return {
        hasOverlap: overlappingSlots.length > 0,
        overlappingSlots
      };
    } catch (error) {
      console.error('Error checking overlap:', error);
      return { hasOverlap: false, overlappingSlots: [] };
    }
  };

  const handleCreateBlock = async (e: React.FormEvent) => {
    e.preventDefault();
    setBlockFormError('');
    
    if (!newBlock.date || selectedHours.length === 0) {
      setBlockFormError('Please select a date and at least one hour block');
      return;
    }

    // Check for overlaps with existing bookings
    const { hasOverlap, overlappingSlots } = await checkOverlapWithBookings(newBlock.date, selectedHours);
    
    if (hasOverlap) {
      setBlockFormError(
        `Cannot block the selected time(s). The following slots overlap with existing call bookings: ${overlappingSlots.join(', ')}`
      );
      return;
    }

    try {
      // Create blocks for each selected hour
      for (const hour of selectedHours.sort((a, b) => a - b)) {
        const startTime = `${hour.toString().padStart(2, '0')}:00`;
        const endTime = `${(hour + 1).toString().padStart(2, '0')}:00`;
        
        await onboardingCallsService.createScheduleBlock(
          newBlock.date,
          startTime,
          endTime,
          newBlock.reason || 'Manual block'
        );
      }
      
      await fetchScheduleBlocks();
      setShowBlockForm(false);
      setSelectedHours([]);
      setBlockFormError('');
      setNewBlock({
        date: '',
        reason: ''
      });
      alert('Schedule block(s) created successfully');
    } catch (error) {
      console.error('Error creating schedule block:', error);
      setBlockFormError('Failed to create schedule block. Please try again.');
    }
  };

  const toggleHourSelection = (hour: number) => {
    setSelectedHours(prev => {
      if (prev.includes(hour)) {
        return prev.filter(h => h !== hour);
      } else {
        return [...prev, hour].sort((a, b) => a - b);
      }
    });
  };

  const deleteScheduleBlock = async (blockIds: string[]) => {
    if (!window.confirm('Are you sure you want to delete this schedule block?')) {
      return;
    }

    try {
      // Delete all blocks in the consolidated group
      for (const blockId of blockIds) {
        await onboardingCallsService.deleteScheduleBlock(blockId);
      }
      await fetchScheduleBlocks();
      alert('Schedule block deleted successfully');
    } catch (error) {
      console.error('Error deleting schedule block:', error);
      alert('Failed to delete schedule block. Please try again.');
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      fullDate: date.toISOString().split('T')[0]
    };
  };

  const groupCallsByDate = () => {
    const grouped: Record<string, OnboardingCall[]> = {};
    scheduledCalls.forEach(call => {
      const { fullDate } = formatDateTime(call.selected_time_slot);
      if (!grouped[fullDate]) {
        grouped[fullDate] = [];
      }
      grouped[fullDate].push(call);
    });
    return grouped;
  };

  const callsByDate = groupCallsByDate();
  const sortedDates = Object.keys(callsByDate).sort();

  const getMonthDisplay = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const getDayDisplay = (dateStr: string) => {
    const date = new Date(dateStr);
    return {
      day: date.getDate(),
      weekday: date.toLocaleDateString('en-US', { weekday: 'short' })
    };
  };

  let currentMonth = '';

  // Generate hours from 9 to 18 (9 AM to 6 PM)
  const availableHours = Array.from({ length: 10 }, (_, i) => i + 9);

  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header Section */}
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Scheduled Onboarding Calls</h1>
          <p className="text-gray-600">Calendar view of all upcoming partner onboarding sessions</p>
        </div>
        <Button
          onClick={() => setShowBlockForm(true)}
          className="bg-purple-600 hover:bg-purple-700 text-white"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Schedule Block
        </Button>
      </div>

      {/* Schedule Blocks Section */}
      {scheduleBlocks.length > 0 && (
        <div className="bg-yellow-50 rounded-lg shadow-sm border border-yellow-200 p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Calendar className="w-5 h-5 mr-2 text-yellow-600" />
            Active Manual Schedule Blocks
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {scheduleBlocks.map((block) => (
              <div key={block.id} className="bg-white rounded-lg p-4 border border-yellow-300">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">
                      {new Date(block.blocked_date).toLocaleDateString('en-US', { 
                        weekday: 'short', 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </p>
                    <p className="text-sm text-gray-600">
                      {block.start_time} - {block.end_time}
                      {block.hours_count > 1 && (
                        <span className="ml-2 text-xs text-purple-600 font-medium">
                          ({block.hours_count} hours)
                        </span>
                      )}
                    </p>
                  </div>
                  <button
                    onClick={() => deleteScheduleBlock(block.ids)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                {block.reason && (
                  <p className="text-xs text-gray-500 mt-2">{block.reason}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Calendar View */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6">
          {loading ? (
            <div className="text-center py-12 text-gray-500">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p>Loading scheduled calls...</p>
            </div>
          ) : scheduledCalls.length === 0 ? (
            <div className="text-center py-12">
              <CalendarIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No scheduled calls at this time</p>
            </div>
          ) : (
            <div className="space-y-6">
              {sortedDates.map((dateStr) => {
                const monthDisplay = getMonthDisplay(dateStr);
                const showMonthHeader = monthDisplay !== currentMonth;
                if (showMonthHeader) {
                  currentMonth = monthDisplay;
                }

                return (
                  <div key={dateStr}>
                    {/* Month Header */}
                    {showMonthHeader && (
                      <div className="mb-4 pb-2 border-b-2 border-blue-600">
                        <h2 className="text-xl font-bold text-gray-900">{monthDisplay}</h2>
                      </div>
                    )}

                    {/* Date Row */}
                    <div className="flex gap-4">
                      {/* Date Badge */}
                      <div className="flex-shrink-0 w-16 text-center">
                        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-2">
                          <div className="text-xs font-semibold text-blue-600 uppercase">
                            {getDayDisplay(dateStr).weekday}
                          </div>
                          <div className="text-2xl font-bold text-gray-900 mt-1">
                            {getDayDisplay(dateStr).day}
                          </div>
                        </div>
                      </div>

                      {/* Calls for this date */}
                      <div className="flex-1 space-y-3">
                        {callsByDate[dateStr].map((call) => {
                          const { time } = formatDateTime(call.selected_time_slot);
                          return (
                            <div 
                              key={call.id} 
                              className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow bg-white"
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-3 mb-3">
                                    <Clock className="w-5 h-5 text-blue-600" />
                                    <span className="text-sm font-semibold text-blue-600">{time}</span>
                                    <span className="text-sm text-gray-500">•</span>
                                    <h3 className="text-lg font-semibold text-gray-900">
                                      {call.business_name}
                                    </h3>
                                  </div>
                                  
                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm text-gray-600">
                                    <div className="flex items-center">
                                      <User className="w-4 h-4 mr-2 text-gray-400" />
                                      <span>{call.contact_name}</span>
                                    </div>
                                    <div className="flex items-center">
                                      <Mail className="w-4 h-4 mr-2 text-gray-400" />
                                      <span>{call.contact_email}</span>
                                    </div>
                                    <div className="flex items-center">
                                      <Phone className="w-4 h-4 mr-2 text-gray-400" />
                                      <span>{call.contact_phone}</span>
                                    </div>
                                  </div>

                                  {call.onboarding_call_notes && (
                                    <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                                      <p className="text-sm text-gray-700">{call.onboarding_call_notes}</p>
                                    </div>
                                  )}
                                </div>
                                
                                <button
                                  onClick={() => handleDeleteCall(call.id)}
                                  className="ml-4 text-red-600 hover:text-red-800 p-2 rounded-lg hover:bg-red-50 transition-colors"
                                  title="Delete call"
                                >
                                  <Trash2 className="w-5 h-5" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Schedule Block Modal */}
      {showBlockForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-900">Create Schedule Block</h2>
              <button
                onClick={() => {
                  setShowBlockForm(false);
                  setBlockFormError('');
                  setSelectedHours([]);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {blockFormError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">{blockFormError}</p>
              </div>
            )}

            <form onSubmit={handleCreateBlock} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date *
                </label>
                <input
                  type="date"
                  value={newBlock.date}
                  onChange={(e) => setNewBlock({ ...newBlock, date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Hour Blocks * (9:00 - 18:00)
                </label>
                <div className="grid grid-cols-5 gap-2 max-h-48 overflow-y-auto p-2 border border-gray-200 rounded-lg">
                  {availableHours.map((hour) => {
                    const isSelected = selectedHours.includes(hour);
                    return (
                      <button
                        key={hour}
                        type="button"
                        onClick={() => toggleHourSelection(hour)}
                        className={`
                          px-3 py-2 rounded-lg text-sm font-medium transition-colors
                          ${isSelected 
                            ? 'bg-purple-600 text-white hover:bg-purple-700' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }
                        `}
                      >
                        {hour}:00
                      </button>
                    );
                  })}
                </div>
                {selectedHours.length > 0 && (
                  <p className="text-xs text-gray-600 mt-2">
                    Selected: {selectedHours.map(h => `${h}:00`).join(', ')}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason (Optional)
                </label>
                <textarea
                  value={newBlock.reason}
                  onChange={(e) => setNewBlock({ ...newBlock, reason: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  rows={3}
                  placeholder="e.g., Out of office, Team meeting"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  onClick={() => {
                    setShowBlockForm(false);
                    setBlockFormError('');
                    setSelectedHours([]);
                  }}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                >
                  Create Block
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerOnboardingCalls;